import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// AuthCallback handles the native Google OAuth return:
//   ?exchange_id=...    The DH-branded native flow (only supported path).
// The one-shot exchange_id is traded for a session JWT, then we route the
// user to /dashboard (or /pricing for pending_billing accounts).
export default function AuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const exchangeId = params.get('exchange_id');

    if (exchangeId) {
      (async () => {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const resp = await axios.post(`${API_URL}/api/auth/google/exchange`, {
            exchange_id: exchangeId,
          });
          const { token, user } = resp.data;
          setSession(token, user);
          toast.success('Welcome');
          navigate(user?.billing_status === 'pending_billing' ? '/pricing' : '/dashboard', { replace: true });
        } catch (e) {
          const detail = e?.response?.data?.detail || 'Google sign-in failed';
          toast.error(typeof detail === 'string' ? detail : 'Google sign-in failed');
          navigate('/login', { replace: true });
        }
      })();
      return;
    }

    toast.error('Missing session — please try signing in again.');
    navigate('/login', { replace: true });
  }, [params, navigate, setSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback-page">
      <div className="text-center space-y-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground uppercase tracking-widest">Signing you in…</p>
      </div>
    </div>
  );
}
